/**
 * Established canon and approved public copy, from "The King Arachnid Chronicles — Lore and
 * character handoff v1.0" (16 Oct 2026). This is real, creator-owned text — not fixture data.
 *
 * Rules from the handoff: the spiders took over his body, not his mind; Kynorum's loyalty is
 * undecided, not a hidden answer. Do not add powers, backstories, places, episode events,
 * family relationships or explanations here. The open questions are deliberately unanswered.
 */

export const SITE_TITLE = 'The King Arachnid Chronicles';
export const SITE_TAGLINE = 'A changed king. A faithful companion. A world to explore.';
export const SITE_INTRO = [
  "A cave spider's bite changed him. The spiders that followed transformed him into King Arachnid. But inside, he is still himself.",
  'Alongside his faithful companion Kynorum, the adventures continue. Explore their Minecraft world, follow their videos, and discover the story behind the characters.',
];
export const CHARACTERS_HEADING = 'Meet King Arachnid and Kynorum';
export const LORE_HEADING = 'The story so far';

export const KING_ARACHNID_TAGLINE = 'Changed by spiders. Still himself.';
export const KING_ARACHNID_BIO = `King Arachnid was once an ordinary person. Then a cave spider bit him.

After the bite, spiders began appearing around him. As time passed, more and more gathered—until they attacked and took over his body, transforming him into King Arachnid.

But his mind remained his own. He is still the same person inside his changed body.

Beside him stands his faithful companion, Kynorum. Why Kynorum follows him remains a mystery.`;

/** Open mysteries, phrased for visitors. They have no written answers yet. */
export const KING_ARACHNID_UNKNOWNS = [
  'Why were the spiders drawn to him?',
  'Was the bite an accident?',
  'How does his transformation work?',
  'How does he feel about what happened to him?',
];

export const KYNORUM_TAGLINE = 'A faithful companion. A mysterious loyalty.';
export const KYNORUM_BIO = `Kynorum is King Arachnid's faithful companion. Whatever King Arachnid requires, Kynorum carries out.

His loyalty is certain. The reason behind it is a mystery.`;

export const KYNORUM_UNKNOWNS = [
  'What lies behind his loyalty?',
  'How did he and King Arachnid meet?',
];

/** Lore: the origin, told only with confirmed events. */
export const ORIGIN_LORE_TITLE = 'How King Arachnid came to be';
export const ORIGIN_LORE_SLUG = 'how-king-arachnid-came-to-be';
export const ORIGIN_LORE_BODY = `He was once an ordinary person. Then a cave spider bit him.

After the bite, he began to notice spiders appearing around him. Over time, more and more of them came.

Eventually the spiders attacked him and took over his body. That change made him King Arachnid.

His body changed—but his mind did not. He still thinks for himself, makes his own choices, and is still the same person inside.`;

/** Lore: the questions the story has not answered yet (creators will decide). */
export const MYSTERIES_LORE_TITLE = 'Unanswered mysteries';
export const MYSTERIES_LORE_SLUG = 'unanswered-mysteries';
export const MYSTERIES_LORE_BODY = `Some parts of the story are still unknown.

- **The bite.** It came before everything else. Was it an accident, or something else?
- **The gathering.** More and more spiders appeared around him. Why were they drawn to him?
- **The transformation.** His body changed, but his mind stayed his own. Why did it happen, and how does it work?
- **His feelings.** He is still himself. But is he frightened, pleased, or something else?
- **Kynorum's loyalty.** Kynorum follows King Arachnid faithfully. What lies behind that loyalty?

Keep watching. The story is still being written.`;

/** Stable ids so seeding is idempotent across environments. */
export const CANON_IDS = {
  king: 'chr_kingarachnid01',
  kynorum: 'chr_kynorum000001',
  origin: 'lore_origin00000001',
  mysteries: 'lore_mysteries00001',
} as const;

/** The canon drafts, as admin inputs. Shared by the dev seed, fixtures and the AWS seed command. */
export const CANON_DRAFTS = [
  {
    kind: 'characters',
    id: CANON_IDS.king,
    data: {
      slug: 'king-arachnid',
      displayName: 'King Arachnid',
      tagline: KING_ARACHNID_TAGLINE,
      biography: KING_ARACHNID_BIO,
      canonStatus: 'established',
      unknowns: KING_ARACHNID_UNKNOWNS,
      skinAssetId: null,
      modelType: 'classic',
      accent: 'red',
      editorialStatus: 'ready',
    },
  },
  {
    kind: 'characters',
    id: CANON_IDS.kynorum,
    data: {
      slug: 'kynorum',
      displayName: 'Kynorum',
      tagline: KYNORUM_TAGLINE,
      biography: KYNORUM_BIO,
      canonStatus: 'established',
      unknowns: KYNORUM_UNKNOWNS,
      skinAssetId: null,
      modelType: 'classic',
      accent: 'purple',
      editorialStatus: 'ready',
    },
  },
  {
    kind: 'lore',
    id: CANON_IDS.origin,
    data: {
      slug: ORIGIN_LORE_SLUG,
      title: ORIGIN_LORE_TITLE,
      body: ORIGIN_LORE_BODY,
      canonStatus: 'established',
      characterIds: [CANON_IDS.king],
      landmarkIds: [],
      editorialStatus: 'ready',
    },
  },
  {
    kind: 'lore',
    id: CANON_IDS.mysteries,
    data: {
      slug: MYSTERIES_LORE_SLUG,
      title: MYSTERIES_LORE_TITLE,
      body: MYSTERIES_LORE_BODY,
      canonStatus: 'open-question',
      characterIds: [CANON_IDS.king, CANON_IDS.kynorum],
      landmarkIds: [],
      editorialStatus: 'ready',
    },
  },
] as const;
